import { FormikProps } from "formik";
import React from "react";
import ATMTextField from "../UI/atoms/formFields/ATMTextField/ATMTextField";
import { VerificationInitialValues } from "./ApplicationVerifyDialogWrapper";
import ATMLoadingButton from "../UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMFilePickerWrapper from "../UI/atoms/formFields/ATMFileUploader/ATMFileUploaderWrapper";
import AOCodeField from "./AOCodeField";
import { showToast } from "src/utils/toaster/showToast";
import {
  getAcknowledgementNumber,
  isValidFileName,
} from "src/utils/acknowledgementNumber/acknowledgementNumber";

type Props = {
  formikProps: FormikProps<VerificationInitialValues>;
  onClose: () => void;
  type :string
  category:string
  dobProofType?:string,
  paymentCategory?:any
  /** PAN application type — "NEW" or "CORRECTION". */
  applicationType?:string
  /**
   * Whether to ask for the AO Code. True for a NEW PAN application only — see
   * ApplicationVerifyDialogWrapper.
   */
  showAOCode?:boolean
};

const ApplicationVerifyDialog = ({ formikProps, onClose,type, category, dobProofType, paymentCategory, applicationType, showAOCode = type === "PAN" }: Props) => {
  console.log("type", type, category, paymentCategory);
  const { values, setFieldValue, isSubmitting } = formikProps;

  /**
   * Which of the two fields carries the category depends on how the
   * application was priced: `baseCatAppied` is set when DSA or PCO was the
   * base category, while `paymentCategory` lists every category paid for.
   * Entries are `{ code }` objects on current records and were bare strings on
   * older ones, so both shapes are matched. Same rule as PanFormDialog's
   * `hasCategory` — the two decide the same thing about the same application.
   */
  const hasCategory = (code: string) =>
    category === code ||
    (Array.isArray(paymentCategory)
      ? paymentCategory.some((item: any) =>
          typeof item === "string" ? item === code : item?.code === code
        )
      : false);

  const showApplicantFields = type === "PAN" && category === "CAT_DSA";

  /**
   * The applicant's residence address, asked for on a DSA / PCO CORRECTION.
   *
   * Those two are filed over a counter, so their PAN form opens on Office
   * Address — and the correction form has only ONE address block, so on a
   * correction that block is the counter's address and the applicant's own is
   * captured nowhere. The Affidavit and DSA / Employer certificates both need
   * the applicant's residence, so it is typed here, at login, and the form's
   * office address is kept from overwriting it (panFormToCertificate /
   * pan-form.service.ts `applicantFieldsFromFormData`).
   *
   * A NEW application needs none of this: its form has a residential block of
   * its own alongside the office one. Neither does a correction in any other
   * category, whose single block is the residence already.
   */
  const showResidenceAddress =
    showApplicantFields ||
    (type === "PAN" &&
      applicationType === "CORRECTION" &&
      (hasCategory("CAT_DSA") || hasCategory("CAT_PCO")));

  return (
    <div className=" flex flex-col overflow-auto   rounded  bg-white relative ">
      <div className="grow overflow-auto">

        
{/*
          The applicant's name and father's name are NOT asked for here: both
          are typed on the PAN form itself, which is the reviewed source of
          truth, and the API mirrors them onto the application from there
          (pan-form.service.ts `applicantFieldsFromFormData`). Asking again at
          login only gave the operator two chances to type the same two names
          differently.

          The address and Place below are a different case — the DSA / PCO form
          has no residence block to read them off, so this is the only place
          they are ever captured. See `showResidenceAddress`.
        */}

        {/*
          Shown for a DSA / PCO correction too, where it is the ONLY place the
          applicant's own address is ever captured — see `showResidenceAddress`.
        */}
        {showResidenceAddress && (
         <div className="py-3 w-[90%]">
          <ATMTextField
            className=" py-5"
            name="acknowledgement_address"
            value={values.acknowledgement_address}
            onChange={(e) => {
              setFieldValue("acknowledgement_address", e.target.value);
            }}
            label="Residential address"
            placeholder="Residential address"
          />
        </div>
         )}

        {/*
          Gated on the same flag as the address above, not on
          `showApplicantFields`: on a DSA / PCO correction the Place has
          exactly the same problem the address does — the single block on the
          correction form is the counter's, so the applicant's Place is
          captured nowhere else, and the Affidavit / Employer certificate both
          print it.
        */}
        {showResidenceAddress && (
         <div className="py-3 w-[90%]">
          <ATMTextField
            className=" py-5"
            name="acknowledgement_place"
            value={values.acknowledgement_place}
            onChange={(e) => {
              setFieldValue("acknowledgement_place", e.target.value);
            }}
            label="Place"
            placeholder="Place"
          />
        </div>
         )}



        {/* Acknowledgement */}
        <div className="py-2 w-[90%]">
          <ATMFilePickerWrapper
            name="acknowledgement"
            selectedFile={values?.acknowledgement}
            accept="application/pdf"
            onSelect={(newValue: any) => { 
             if(type === 'PAN'){ 
           
              if (isValidFileName(newValue.name)) { 
                setFieldValue("acknowledgement", newValue);
                setFieldValue(
                  "acknowledgementNumber",
                  getAcknowledgementNumber(newValue.name)
                );
              } else {
                showToast("error", "Invalid File name");
                setFieldValue("acknowledgement", "");
              }
             }else{
              setFieldValue("acknowledgement", newValue);
              setFieldValue(
                "acknowledgementNumber",''
              );
             }
            }}
            label="Acknowledgement"
            hideCloseButton
          />
        </div>
        {/*
          A NEW PAN captures the AO Code here instead of a remark: the code is
          looked up at exactly this moment and is what the PAN form's Part D
          cells are pre-filled from. A PAN correction has no Part D, so it falls
          back to the free-text remark every other service takes — AO codes mean
          nothing to them.
        */}
        {showAOCode ? (
          <div className="py-3 w-[90%]">
            <AOCodeField
              values={{
                aoAreaCode: values.aoAreaCode,
                aoType: values.aoType,
                aoRangeCode: values.aoRangeCode,
                aoNo: values.aoNo,
              }}
              onChange={(patch) =>
                Object.entries(patch).forEach(([key, value]) =>
                  setFieldValue(key, value)
                )
              }
            />
          </div>
        ) : (
          <div className="py-3 w-[90%]">
            <ATMTextField
              className=" py-5"
              name="remark"
              value={values.remark}
              onChange={(e) => {
                setFieldValue("remark", e.target.value);
              }}
              label="Remark"
              placeholder="Remark"
            />
          </div>
        )}
      </div>

      <div className="flex justify-end pt-3">
        <div className="flex justify-end gap-3">
          <ATMLoadingButton
            className="border-primary-main text-primary-main bg-white"
            onClick={onClose}
          >
            Cancel
          </ATMLoadingButton>

          <ATMLoadingButton
            type="submit"
            isLoading={isSubmitting}
            className="bg-primary-main"
          >
            Verify
          </ATMLoadingButton>
        </div>
      </div>
    </div>
  );
};

export default ApplicationVerifyDialog;